// src/app/routes/RequireAuth.jsx
import { Navigate, useLocation } from "react-router-dom";
import { useAuthSession } from "../features/auth/useAuth.js";

// ✅ Keep in sync with backend bootstrap admin policy
const BOOTSTRAP_ADMIN_EMAIL = "admin@worldcoinscom.com";

const normalizePath = (location) => {
  const path = location?.pathname || "";
  const search = location?.search || "";
  return `${path}${search}`;
};

const isPrivileged = (user) => {
  const role = (user?.role || "").toUpperCase().trim();
  const email = (user?.email || "").toLowerCase().trim();

  // ✅ Primary: role-based privileged access
  if (role === "ADMIN" || role === "MODERATOR") return true;

  // ✅ Defensive: bootstrap admin email bypass (mirrors backend behavior)
  if (email && email === BOOTSTRAP_ADMIN_EMAIL) return true;

  return false;
};

const isAuthPath = (fullPath) => {
  const p = fullPath || "";

  // Anything under /auth is non-protected flow
  if (p.startsWith("/auth/")) return true;

  // ✅ Support legacy top-level auth routes used by App.jsx
  if (p === "/login" || p.startsWith("/login?")) return true;
  if (p === "/register" || p.startsWith("/register?")) return true;

  // Extra safety for query variations
  return (
    p.includes("/auth/login") ||
    p.includes("/auth/register") ||
    p.includes("/auth/forgot-password") ||
    p.includes("/auth/reset") ||
    p.includes("/auth/kyc-verification") ||
    p.includes("/auth/kyc-pending")
  );
};

export function RequireAuth({ children, requireKyc = false }) { // ✅ Changed default to false
  const { user, session, loading } = useAuthSession();
  const location = useLocation();

  const hasSession = !!session?.accessToken;
  const fromPath = normalizePath(location);

  // ✅ Avoid rendering protected content before we know session state
  if (loading && !hasSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto" />
          <p className="mt-4 text-gray-400">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // ✅ No session => redirect to login, but do NOT loop if already on auth pages
  if (!hasSession) {
    if (isAuthPath(fromPath)) return children;
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // ✅ Session exists but user profile not loaded yet:
  // Allow rendering; do NOT KYC-redirect yet (prevents premature loops/flicker).
  if (!user) return children;

  // ✅ Account checks (always enforced)
  if (user.is_suspended) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-950">
        <div className="max-w-md text-center p-6 rounded-2xl bg-gray-800 border border-red-500/30">
          <div className="text-2xl mb-4 text-red-400">⚠️ Account Suspended</div>
          <p className="text-gray-300 mb-4">
            Your account has been suspended. Please contact support for assistance.
          </p>
          <a
            href="mailto:support@cryptotradex.com"
            className="inline-block px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition"
          >
            Contact Support
          </a>
        </div>
      </div>
    );
  }

  if (user.is_active === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-950">
        <div className="max-w-md text-center p-6 rounded-2xl bg-gray-800 border border-amber-500/30">
          <div className="text-2xl mb-4 text-amber-400">⚠️ Account Inactive</div>
          <p className="text-gray-300 mb-4">
            Your account is not active. Please contact support to reactivate your account.
          </p>
          <a
            href="mailto:support@cryptotradex.com"
            className="inline-block px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition"
          >
            Contact Support
          </a>
        </div>
      </div>
    );
  }

  // ✅ Geo policy enforcement (match backend deps exactly)
  // Backend blocks everyone (including admins) if geo_allowed is false.
  if (user.geo_allowed === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-950">
        <div className="max-w-md text-center p-6 rounded-2xl bg-gray-800 border border-slate-600/40">
          <div className="text-2xl mb-4 text-slate-200">Access Restricted</div>
          <p className="text-gray-300 mb-4">
            Access is not allowed in your region. Please contact support if you believe this is an error.
          </p>
          <a
            href="mailto:support@cryptotradex.com"
            className="inline-block px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition"
          >
            Contact Support
          </a>
        </div>
      </div>
    );
  }

  // ✅ KYC gating - COMPLETELY DISABLED
  // KYC is now auto-verified on login, so we don't need to check it anymore
  // The requireKyc parameter is ignored to prevent any KYC redirects
  
  // ✅ All checks passed - render children
  return children;
}
